import { useCallback, useEffect, useMemo, useState } from 'react';
import { Lock, Unlock, ShieldAlert, CalendarClock, Timer, Plus, X, Users } from 'lucide-react';
import { apiLogTimeLock, apiSettings, apiPersons } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import DatePicker from '../components/DatePicker.jsx';

/**
 * Sprint 6 + Sprint 20 — UI Admin du verrou comptable Logtime.
 *
 * 4 blocs :
 *  1. État effectif (cutoff combiné manuel + auto-lock).
 *  2. Verrou manuel (set / unlock — sentinelle 1900-01-01).
 *  3. Auto-lock glissant (semaine / mois, délai de grâce) — via /api/settings.
 *  4. Exemptions temporaires (déverrouillage d'une plage pour des Persons,
 *     pendant une durée limitée).
 */

const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent)',
  color: '#fff', border: 'none', borderRadius: 'var(--radius-pill)', padding: '8px 16px',
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
};
const secondaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
  color: 'var(--text)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-pill)',
  padding: '6px 12px', fontSize: 13, cursor: 'pointer',
};
const dangerBtn = { ...secondaryBtn, color: 'var(--danger)', borderColor: 'var(--danger)' };
const inputStyle = {
  width: '100%', padding: '8px 10px', background: 'var(--glass)', color: 'var(--text)',
  border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', fontSize: 13,
};
const card = {
  marginTop: 20, background: 'var(--card-bg)', border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-md)', padding: '20px 24px', boxShadow: 'var(--shadow-card)',
};
const sectionTitle = { margin: 0, fontSize: 15, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 };

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch { return String(d); }
}
function formatDateTime(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('fr-FR'); } catch { return String(d); }
}
function personLabel(p) {
  if (!p) return 'Toutes les personnes';
  return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.id;
}

// ---- Helpers de plages de dates (UTC) ----
function toIso(d) { return d.toISOString().slice(0, 10); }
function prevWeekRange() {
  const now = new Date();
  const dow = (now.getUTCDay() + 6) % 7; // 0=Lundi
  const thisMon = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - dow));
  const lastMon = new Date(thisMon); lastMon.setUTCDate(thisMon.getUTCDate() - 7);
  const lastSun = new Date(lastMon); lastSun.setUTCDate(lastMon.getUTCDate() + 6);
  return [toIso(lastMon), toIso(lastSun)];
}
function prevMonthRange() {
  const now = new Date();
  const lastPrev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  const firstPrev = new Date(Date.UTC(lastPrev.getUTCFullYear(), lastPrev.getUTCMonth(), 1));
  return [toIso(firstPrev), toIso(lastPrev)];
}

const DURATION_PRESETS = [
  { label: '30 min', minutes: 30 },
  { label: '1 h', minutes: 60 },
  { label: '2 h', minutes: 120 },
  { label: '4 h', minutes: 240 },
  { label: '1 jour', minutes: 1440 },
  { label: '7 jours', minutes: 10080 },
];

export default function LogTimeLockView({ user }) {
  const isAdmin = user?.role === 'ADMIN';
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Verrou manuel
  const [setModalOpen, setSetModalOpen] = useState(false);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [lockDate, setLockDate] = useState('');
  const [reason, setReason] = useState('');

  // Auto-lock (édité localement, sauvé via apiSettings)
  const [auto, setAuto] = useState(null);

  // Grants
  const [persons, setPersons] = useState([]);
  const [grantModalOpen, setGrantModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiLogTimeLock.current();
      setState(data);
      setAuto({
        weekEnabled: data?.autoLock?.week?.enabled ?? false,
        weekGraceDays: data?.autoLock?.week?.graceDays ?? 3,
        monthEnabled: data?.autoLock?.month?.enabled ?? false,
        monthGraceDays: data?.autoLock?.month?.graceDays ?? 5,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isAdmin) return;
    apiPersons.list('active').then((r) => setPersons(r?.items ?? r ?? [])).catch(() => {});
  }, [isAdmin]);

  const manualActive = useMemo(() => {
    const raw = state?.manual?.rawLockDate;
    return raw ? new Date(raw).getFullYear() >= 1990 : false;
  }, [state]);

  const openSetModal = () => {
    setLockDate(
      manualActive && state?.manual?.lockDate
        ? state.manual.lockDate
        : new Date().toISOString().slice(0, 10),
    );
    setReason('');
    setSaveError(null);
    setSetModalOpen(true);
  };

  const doSet = async () => {
    setSaving(true); setSaveError(null);
    try {
      await apiLogTimeLock.set(lockDate, reason.trim() || null);
      setSetModalOpen(false);
      await load();
    } catch (e) { setSaveError(e.message); } finally { setSaving(false); }
  };

  const doUnlock = async () => {
    setSaving(true); setSaveError(null);
    try {
      await apiLogTimeLock.set('1900-01-01', 'Déverrouillage manuel');
      setUnlockModalOpen(false);
      await load();
    } catch (e) { setSaveError(e.message); } finally { setSaving(false); }
  };

  const saveAuto = async () => {
    setSaving(true); setSaveError(null);
    try {
      await apiSettings.update({
        logtimeAutoLockWeekEnabled: auto.weekEnabled,
        logtimeAutoLockWeekGraceDays: Number(auto.weekGraceDays),
        logtimeAutoLockMonthEnabled: auto.monthEnabled,
        logtimeAutoLockMonthGraceDays: Number(auto.monthGraceDays),
      });
      await load();
    } catch (e) { setSaveError(e.message); } finally { setSaving(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm('Révoquer cette exemption ? La période se re-verrouille immédiatement.')) return;
    try { await apiLogTimeLock.revokeGrant(id); await load(); }
    catch (e) { window.alert(e.message); }
  };

  if (!isAdmin) {
    return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Accès réservé aux administrateurs.</div>;
  }
  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Chargement…</div>;
  if (error) return <div style={{ padding: 32, color: 'var(--danger)' }}>{error}</div>;

  const effective = state?.effectiveCutoff ?? null;
  const grants = state?.grants ?? [];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 940, margin: '0 auto' }}>
      <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <ShieldAlert size={20} /> Verrou Logtime
      </h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>
        Le verrou empêche toute modification des saisies horaires antérieures à une date.
        Un ADMIN peut toujours corriger (bypass intégral). Les autres rôles peuvent recevoir
        une exemption temporaire pour rattraper une période.
      </p>

      {/* 1. État effectif */}
      <div style={card}>
        {effective ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)' }}>
            <Lock size={18} />
            <strong style={{ fontSize: 16 }}>
              Saisies gelées jusqu&apos;au {formatDate(effective)} (inclus)
            </strong>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--success)' }}>
            <Unlock size={18} /> <strong style={{ fontSize: 16 }}>Aucun verrou actif</strong>
          </div>
        )}
        <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-muted)', display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 4 }}>
          <span>Verrou manuel :</span>
          <span style={{ color: 'var(--text)' }}>{manualActive ? `jusqu'au ${formatDate(state.manual.lockDate)}` : '—'}</span>
          <span>Auto-lock semaine :</span>
          <span style={{ color: 'var(--text)' }}>
            {state?.autoLock?.week?.enabled
              ? `actif (cutoff ${formatDate(state.autoLock.week.cutoff)})`
              : 'désactivé'}
          </span>
          <span>Auto-lock mois :</span>
          <span style={{ color: 'var(--text)' }}>
            {state?.autoLock?.month?.enabled
              ? `actif (cutoff ${formatDate(state.autoLock.month.cutoff)})`
              : 'désactivé'}
          </span>
        </div>
      </div>

      {/* 2. Verrou manuel */}
      <div style={card}>
        <h2 style={sectionTitle}><Lock size={16} /> Verrou manuel</h2>
        {manualActive ? (
          <dl style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 6, fontSize: 13 }}>
            <dt style={{ color: 'var(--text-muted)' }}>Jusqu&apos;au :</dt>
            <dd style={{ color: 'var(--text)', margin: 0 }}><strong>{formatDate(state.manual.lockDate)}</strong></dd>
            <dt style={{ color: 'var(--text-muted)' }}>Posé par :</dt>
            <dd style={{ color: 'var(--text)', margin: 0 }}>
              {state.manual.lockedBy ? `${state.manual.lockedBy.name ?? state.manual.lockedBy.email}` : '—'}
            </dd>
            <dt style={{ color: 'var(--text-muted)' }}>Le :</dt>
            <dd style={{ color: 'var(--text)', margin: 0 }}>{formatDateTime(state.manual.lockedAt)}</dd>
            <dt style={{ color: 'var(--text-muted)' }}>Raison :</dt>
            <dd style={{ color: 'var(--text)', margin: 0 }}>{state.manual.reason ?? '—'}</dd>
          </dl>
        ) : (
          <p style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)' }}>Aucun verrou manuel posé.</p>
        )}
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button type="button" style={primaryBtn} onClick={openSetModal}>
            <Lock size={14} /> {manualActive ? 'Modifier' : 'Verrouiller jusqu\'au…'}
          </button>
          {manualActive ? (
            <button type="button" style={dangerBtn} onClick={() => { setSaveError(null); setUnlockModalOpen(true); }}>
              <Unlock size={14} /> Déverrouiller
            </button>
          ) : null}
        </div>
      </div>

      {/* 3. Auto-lock glissant */}
      <div style={card}>
        <h2 style={sectionTitle}><CalendarClock size={16} /> Verrouillage automatique</h2>
        <p style={{ marginTop: 8, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Ferme automatiquement les périodes passées après un délai de grâce. Recalculé en
          continu — aucune action manuelle requise.
        </p>
        {auto ? (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <AutoRow
              label="Semaine"
              hint="La semaine (lun→dim) est gelée N jours après le dimanche."
              enabled={auto.weekEnabled}
              grace={auto.weekGraceDays}
              maxGrace={60}
              onToggle={(v) => setAuto((a) => ({ ...a, weekEnabled: v }))}
              onGrace={(v) => setAuto((a) => ({ ...a, weekGraceDays: v }))}
            />
            <AutoRow
              label="Mois"
              hint="Le mois est gelé N jours après son dernier jour."
              enabled={auto.monthEnabled}
              grace={auto.monthGraceDays}
              maxGrace={90}
              onToggle={(v) => setAuto((a) => ({ ...a, monthEnabled: v }))}
              onGrace={(v) => setAuto((a) => ({ ...a, monthGraceDays: v }))}
            />
            <div>
              <button type="button" style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }} onClick={saveAuto} disabled={saving}>
                Enregistrer la politique
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* 4. Exemptions temporaires */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={sectionTitle}><Timer size={16} /> Exemptions temporaires</h2>
          <button type="button" style={primaryBtn} onClick={() => { setSaveError(null); setGrantModalOpen(true); }}>
            <Plus size={14} /> Nouvelle exemption
          </button>
        </div>
        <p style={{ marginTop: 8, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Rouvre une plage de dates verrouillée pour une/des personne(s), pendant une durée
          limitée. Passé le délai, la période se re-verrouille automatiquement.
        </p>
        {grants.length === 0 ? (
          <p style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)' }}>Aucune exemption active.</p>
        ) : (
          <table style={{ marginTop: 12, width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={{ padding: '6px 8px' }}>Personne</th>
                <th style={{ padding: '6px 8px' }}>Plage rouverte</th>
                <th style={{ padding: '6px 8px' }}>Expire</th>
                <th style={{ padding: '6px 8px' }}>Par</th>
                <th style={{ padding: '6px 8px' }}></th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => (
                <tr key={g.id} style={{ borderTop: '1px solid var(--glass-border)', color: 'var(--text)' }}>
                  <td style={{ padding: '6px 8px' }}>
                    {g.personId == null
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Users size={13} /> Tout le monde</span>
                      : personLabel(g.person)}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{formatDate(g.fromDate)} → {formatDate(g.toDate)}</td>
                  <td style={{ padding: '6px 8px' }}>{formatDateTime(g.expiresAt)}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>{g.createdBy?.name ?? g.createdBy?.email ?? '—'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <button type="button" style={{ ...dangerBtn, padding: '4px 10px' }} onClick={() => revoke(g.id)}>
                      Révoquer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modale set verrou manuel */}
      <Modal
        open={setModalOpen}
        onClose={() => setSetModalOpen(false)}
        title="Verrouiller les saisies horaires"
        footer={
          <>
            <button type="button" style={secondaryBtn} onClick={() => setSetModalOpen(false)} disabled={saving}>Annuler</button>
            <button type="button" style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }} onClick={doSet} disabled={saving || !lockDate}>
              <Lock size={14} /> Verrouiller
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            Toute saisie dont la date est <strong>inférieure ou égale</strong> à la date choisie sera
            en lecture seule pour les non-admins (un ADMIN peut toujours corriger).
          </p>
          <label style={{ fontSize: 13, color: 'var(--text)' }}>
            Date de verrou (inclusive)
            <DatePicker value={lockDate} onChange={setLockDate} style={{ ...inputStyle, marginTop: 6 }} />
          </label>
          <label style={{ fontSize: 13, color: 'var(--text)' }}>
            Raison (optionnel)
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={3}
              placeholder="Ex: clôture comptable avril 2026" style={{ ...inputStyle, marginTop: 6, fontFamily: 'inherit', resize: 'vertical' }} />
          </label>
          {saveError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{saveError}</div> : null}
        </div>
      </Modal>

      {/* Modale unlock */}
      <Modal
        open={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        title="Confirmer le déverrouillage"
        footer={
          <>
            <button type="button" style={secondaryBtn} onClick={() => setUnlockModalOpen(false)} disabled={saving}>Annuler</button>
            <button type="button" style={{ ...dangerBtn, opacity: saving ? 0.6 : 1 }} onClick={doUnlock} disabled={saving}>
              <Unlock size={14} /> Déverrouiller
            </button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text)' }}>
          Retire le verrou manuel (l&apos;auto-lock reste actif s&apos;il est configuré). L&apos;historique
          des verrous est conservé.
        </p>
        {saveError ? <div style={{ marginTop: 8, color: 'var(--danger)', fontSize: 13 }}>{saveError}</div> : null}
      </Modal>

      {/* Modale création exemption */}
      <GrantModal
        open={grantModalOpen}
        onClose={() => setGrantModalOpen(false)}
        persons={persons}
        onCreated={async () => { setGrantModalOpen(false); await load(); }}
      />
    </div>
  );
}

function AutoRow({ label, hint, enabled, grace, maxGrace, onToggle, onGrace }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={() => onToggle(!enabled)}
        style={{
          ...secondaryBtn,
          background: enabled ? 'var(--accent)' : 'transparent',
          color: enabled ? '#fff' : 'var(--text)',
          borderColor: enabled ? 'var(--accent)' : 'var(--glass-border)',
          minWidth: 130, justifyContent: 'center',
        }}
      >
        {label} : {enabled ? 'activé' : 'désactivé'}
      </button>
      <label style={{ fontSize: 13, color: enabled ? 'var(--text)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
        Délai de grâce
        <input
          type="number" min={0} max={maxGrace} value={grace} disabled={!enabled}
          onChange={(e) => onGrace(e.target.value)}
          style={{ ...inputStyle, width: 72 }}
        />
        jours
      </label>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1, minWidth: 200 }}>{hint}</span>
    </div>
  );
}

function GrantModal({ open, onClose, persons, onCreated }) {
  const [allPersons, setAllPersons] = useState(false);
  const [selected, setSelected] = useState([]); // [{id, label}]
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      const [f, t] = prevWeekRange();
      setAllPersons(false); setSelected([]); setQ(''); setFrom(f); setTo(t);
      setDurationMinutes(60); setReason(''); setErr(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const selIds = new Set(selected.map((s) => s.id));
    return (persons ?? [])
      .filter((p) => !selIds.has(p.id))
      .filter((p) => {
        if (!needle) return true;
        return personLabel(p).toLowerCase().includes(needle) || (p.email ?? '').toLowerCase().includes(needle);
      })
      .slice(0, 8);
  }, [persons, q, selected]);

  const applyPreset = (kind) => {
    const [f, t] = kind === 'week' ? prevWeekRange() : prevMonthRange();
    setFrom(f); setTo(t);
  };

  const submit = async () => {
    setSaving(true); setErr(null);
    try {
      const payload = {
        fromDate: from, toDate: to, durationMinutes: Number(durationMinutes),
        reason: reason.trim() || null,
        ...(allPersons ? { allPersons: true } : { personIds: selected.map((s) => s.id) }),
      };
      await apiLogTimeLock.createGrant(payload);
      await onCreated();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const canSubmit = from && to && (allPersons || selected.length > 0) && Number(durationMinutes) >= 5;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nouvelle exemption de déverrouillage"
      footer={
        <>
          <button type="button" style={secondaryBtn} onClick={onClose} disabled={saving}>Annuler</button>
          <button type="button" style={{ ...primaryBtn, opacity: saving || !canSubmit ? 0.6 : 1 }} onClick={submit} disabled={saving || !canSubmit}>
            <Unlock size={14} /> Ouvrir l&apos;exemption
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Cible */}
        <div>
          <span style={{ fontSize: 13, color: 'var(--text)', display: 'block', marginBottom: 6 }}>Personnes concernées</span>
          <button
            type="button"
            onClick={() => { setAllPersons((v) => !v); if (!allPersons) setSelected([]); }}
            style={{ ...secondaryBtn, background: allPersons ? 'var(--accent)' : 'transparent', color: allPersons ? '#fff' : 'var(--text)', borderColor: allPersons ? 'var(--accent)' : 'var(--glass-border)' }}
          >
            <Users size={14} /> Tout le monde
          </button>
          {!allPersons ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {selected.map((s) => (
                  <span key={s.id} style={{ ...secondaryBtn, padding: '4px 8px', cursor: 'default' }}>
                    {s.label}
                    <X size={12} style={{ cursor: 'pointer' }} onClick={() => setSelected((cur) => cur.filter((x) => x.id !== s.id))} />
                  </span>
                ))}
              </div>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une personne…" style={inputStyle} />
              {q.trim() && filtered.length > 0 ? (
                <div style={{ marginTop: 6, border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                  {filtered.map((p) => (
                    <button
                      key={p.id} type="button"
                      onClick={() => { setSelected((cur) => [...cur, { id: p.id, label: personLabel(p) }]); setQ(''); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'transparent', border: 'none', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }}
                    >
                      {personLabel(p)} <span style={{ color: 'var(--text-muted)' }}>{p.email ?? ''}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Plage de dates */}
        <div>
          <span style={{ fontSize: 13, color: 'var(--text)', display: 'block', marginBottom: 6 }}>Plage de dates rouverte</span>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button type="button" style={secondaryBtn} onClick={() => applyPreset('week')}>Semaine préc.</button>
            <button type="button" style={secondaryBtn} onClick={() => applyPreset('month')}>Mois préc.</button>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 1 }}>
              Du
              <DatePicker value={from} onChange={setFrom} style={{ ...inputStyle, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 1 }}>
              Au
              <DatePicker value={to} onChange={setTo} style={{ ...inputStyle, marginTop: 4 }} />
            </label>
          </div>
        </div>

        {/* Durée de validité */}
        <div>
          <span style={{ fontSize: 13, color: 'var(--text)', display: 'block', marginBottom: 6 }}>Durée de l&apos;exemption</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {DURATION_PRESETS.map((d) => (
              <button
                key={d.minutes} type="button"
                onClick={() => setDurationMinutes(d.minutes)}
                style={{ ...secondaryBtn, background: durationMinutes === d.minutes ? 'var(--accent)' : 'transparent', color: durationMinutes === d.minutes ? '#fff' : 'var(--text)', borderColor: durationMinutes === d.minutes ? 'var(--accent)' : 'var(--glass-border)' }}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <label style={{ fontSize: 13, color: 'var(--text)' }}>
          Raison (optionnel)
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={2}
            placeholder="Ex: rattrapage saisie semaine 22" style={{ ...inputStyle, marginTop: 6, fontFamily: 'inherit', resize: 'vertical' }} />
        </label>

        {err ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div> : null}
      </div>
    </Modal>
  );
}
